/* eslint-disable react/no-unstable-nested-components */
import React, { useState } from "react";
import { apple, ORANGE, GREEN, BLUE, RED, YELLOW, PURPLE, Card, KpiTile, Badge, PageHeader, Btn, ChipRow, Table, StatusPill, Sparkline, MiniBars, Donut, CountUp, Section, TabBar, Icons } from "./shared";

/* ============================================================
   9. INVOICES / AR with aging buckets
   ============================================================ */
export function Invoices() {
  return (
    <div>
      <PageHeader kicker="ACCOUNTS RECEIVABLE" title="Invoices" subtitle="138 open · $284K outstanding · 4 disputes · avg DSO 18 days"
        actions={<><Btn>Statements</Btn><Btn primary>+ New Invoice</Btn></>} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 16 }}>
        {[
          { l: "0 – 30 days", v: "$182K", n: 84, c: GREEN },
          { l: "31 – 60 days", v: "$58K", n: 32, c: YELLOW },
          { l: "61 – 90 days", v: "$28K", n: 14, c: ORANGE },
          { l: "90+ days", v: "$16K", n: 8, c: RED },
          { l: "DSO", v: "18 days", n: "Target 25", c: BLUE, hero: true },
        ].map((b, i) => (
          <div key={i} style={{ background: b.hero ? "linear-gradient(135deg, rgba(59,130,246,0.12), rgba(59,130,246,0.03))" : "rgba(255,255,255,0.025)", border: b.hero ? `1px solid ${BLUE}66` : `0.5px solid ${b.c}44`, borderRadius: 12, padding: 14, position: "relative", overflow: "hidden" }}>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>{b.l}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "white", letterSpacing: -0.5, marginTop: 4 }}>{b.v}</div>
            <div style={{ fontSize: 10.5, color: b.c, fontWeight: 700, marginTop: 4 }}>{typeof b.n === "number" ? `${b.n} invoices` : b.n}</div>
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: b.c, opacity: 0.6 }} />
          </div>
        ))}
      </div>
      <ChipRow chips={["All · 138", "Open · 138", "Paid this month · 64", "Disputed · 4", "Overdue · 22"]} />
      <Table columns={[
        { key: "id", label: "Invoice", bold: true },
        { key: "cust", label: "Customer" },
        { key: "ship", label: "Shipment" },
        { key: "issued", label: "Issued" },
        { key: "due", label: "Due" },
        { key: "age", label: "Aging", cell: (r) => <Badge color={r.ageC}>{r.age}</Badge> },
        { key: "amt", label: "Amount", align: "right", cell: (r) => <span style={{ fontWeight: 700 }}>{r.amt}</span> },
        { key: "status", label: "Status", cell: (r) => <Badge color={r.c}>{r.status}</Badge> },
        { key: "act", label: "", align: "right", cell: () => <Btn size="sm">Open</Btn> },
      ]} rows={[
        { id: "INV-000189", cust: "ABC Manufacturing", ship: "RY-78432", issued: "May 20", due: "Jun 19", age: "Open", ageC: GREEN, amt: "$4,250", status: "Sent", c: BLUE },
        { id: "INV-000188", cust: "Global Tech", ship: "RY-78321", issued: "May 18", due: "Jun 2", age: "Open", ageC: GREEN, amt: "$2,875", status: "Sent", c: BLUE },
        { id: "INV-000177", cust: "Sunrise Dist.", ship: "RY-78211", issued: "Apr 30", due: "May 7", age: "31-60", ageC: YELLOW, amt: "$3,960", status: "Reminded", c: YELLOW },
        { id: "INV-000172", cust: "Retail Supply", ship: "RY-78112", issued: "Apr 22", due: "May 22", age: "31-60", ageC: YELLOW, amt: "$1,125", status: "Sent", c: BLUE },
        { id: "INV-000158", cust: "ABC Manufacturing", ship: "RY-78198", issued: "Mar 28", due: "Apr 27", age: "Paid", ageC: GREEN, amt: "$4,250", status: "Paid", c: GREEN },
        { id: "INV-000142", cust: "Acme Mfg.", ship: "RY-78021", issued: "Mar 12", due: "Apr 11", age: "61-90", ageC: ORANGE, amt: "$2,420", status: "Dispute", c: RED },
        { id: "INV-000118", cust: "Metro Supply Co.", ship: "RY-77845", issued: "Feb 20", due: "Mar 22", age: "90+", ageC: RED, amt: "$1,840", status: "Collections", c: RED },
        { id: "INV-000094", cust: "Northwest Goods", ship: "RY-77622", issued: "Jan 30", due: "Mar 1", age: "90+", ageC: RED, amt: "$3,210", status: "Legal hold", c: RED },
      ]} />
    </div>
  );
}

/* ============================================================
   10. PAYOUTS / DRIVER SETTLEMENTS
   ============================================================ */
export function Payouts() {
  return (
    <div>
      <PageHeader kicker="DRIVER SETTLEMENTS" title="Payouts" subtitle="Next payout run Wed 3:00 PM EST · 148 drivers · $284K queued"
        actions={<><Btn>Schedule</Btn><Btn primary>Run Payouts Now</Btn></>} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
        <KpiTile label="Queued Payouts" value="$284K" delta="148 drivers" icon={Icons.payouts} />
        <KpiTile label="Factored" value="$84K" delta="32 drivers · instant" icon={Icons.bolt} deltaColor={ORANGE} />
        <KpiTile label="Deductions" value="$42K" delta="Fuel card $32K, advances $10K" icon={Icons.fuel} />
        <KpiTile hero label="MTD Paid" value="$1.18M" delta="↗ +14%" icon={Icons.dollar} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
        <Card title="Payout Schedule">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              { d: "Wed, May 21 · 3:00 PM", n: "148 drivers · $284K", st: "Next", c: ORANGE },
              { d: "Wed, May 14 · 3:00 PM", n: "142 drivers · $268K", st: "Completed", c: GREEN },
              { d: "Wed, May 7 · 3:00 PM", n: "139 drivers · $258K", st: "Completed", c: GREEN },
              { d: "Wed, Apr 30 · 3:00 PM", n: "136 drivers · $252K", st: "Completed", c: GREEN },
            ].map((p, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", background: i === 0 ? `${ORANGE}10` : "rgba(255,255,255,0.025)", border: `0.5px solid ${i === 0 ? ORANGE : "rgba(255,255,255,0.08)"}`, borderRadius: 9 }}>
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "white" }}>{p.d}</div>
                  <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)" }}>{p.n}</div>
                </div>
                <Badge color={p.c}>{p.st}</Badge>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Factoring vs Direct">
          <div style={{ display: "flex", gap: 14, alignItems: "center", padding: "10px 0" }}>
            <Donut pct={71} color={ORANGE} size={140} thickness={14} label="71% direct" />
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 12 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: ORANGE }} />Direct (Wed)</span>
                <span style={{ fontWeight: 700 }}>$201K · 116</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 12 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: "rgba(255,255,255,0.2)" }} />Factored (instant)</span>
                <span style={{ fontWeight: 700 }}>$84K · 32</span>
              </div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginTop: 10, lineHeight: 1.5 }}>RY+ Gold members get free instant pay. Saves drivers $4,200/yr on factoring fees.</div>
            </div>
          </div>
        </Card>
      </div>
      <Card title="Settlement Queue · Wed May 21">
        <Table columns={[
          { key: "driver", label: "Driver", bold: true },
          { key: "loads", label: "Loads" },
          { key: "miles", label: "Miles" },
          { key: "gross", label: "Gross", align: "right" },
          { key: "deduct", label: "Fuel/Adv.", align: "right", cell: (r) => <span style={{ color: RED }}>−{r.deduct}</span> },
          { key: "net", label: "Net Payout", align: "right", cell: (r) => <span style={{ color: GREEN, fontWeight: 800 }}>{r.net}</span> },
          { key: "method", label: "Method", cell: (r) => <Badge color={r.mc}>{r.method}</Badge> },
        ]} rows={[
          { driver: "D. Johnson", loads: 7, miles: "4,820", gross: "$5,160", deduct: "$340", net: "$4,820", method: "Instant (Gold)", mc: YELLOW },
          { driver: "D. Johnson", loads: 6, miles: "3,940", gross: "$4,250", deduct: "$520", net: "$3,730", method: "Instant (Gold)", mc: YELLOW },
          { driver: "D. Johnson", loads: 5, miles: "2,840", gross: "$3,180", deduct: "$280", net: "$2,900", method: "Direct ACH", mc: ORANGE },
          { driver: "D. Johnson", loads: 8, miles: "5,820", gross: "$6,180", deduct: "$1,140", net: "$5,040", method: "Direct ACH", mc: ORANGE },
          { driver: "D. Johnson", loads: 6, miles: "4,140", gross: "$5,420", deduct: "$420", net: "$5,000", method: "Instant (Gold)", mc: YELLOW },
          { driver: "D. Johnson", loads: 4, miles: "2,210", gross: "$2,480", deduct: "$340", net: "$2,140", method: "Direct ACH", mc: ORANGE },
        ]} />
      </Card>
    </div>
  );
}

/* ============================================================
   11. MARGIN MANAGEMENT
   ============================================================ */
export function Margins() {
  return (
    <div>
      <PageHeader kicker="PROFITABILITY" title="Margin Management" subtitle="Real-time margin per shipment · per lane · per customer · floor 18%"
        actions={<><Btn>Floor Settings</Btn><Btn primary>What-If Calculator</Btn></>} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 16 }}>
        <KpiTile label="Org Avg Margin" value="23.4%" delta="↗ +2.1pt" deltaColor={GREEN} icon={Icons.margin} />
        <KpiTile label="Floor (target)" value="≥ 18%" icon={Icons.shield} />
        <KpiTile label="Loads Below Floor" value={4} delta="↓ −3" deltaColor={GREEN} icon={Icons.alert} />
        <KpiTile label="Avg $/Load Margin" value="$612" delta="↗ +$48" icon={Icons.dollar} />
        <KpiTile hero label="Net Profit MTD" value="$902K" delta="↗ +24% · 23.4%" icon={Icons.dollar} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
        <Card title="Margin Distribution · Current MTD">
          <MarginDistribution />
        </Card>
        <Card title="Below-Floor Alerts">
          {[
            { id: "RY-78445", lane: "YYC → DFW", m: "11.2%", reason: "Driver pay overrun (+8%)" },
            { id: "RY-78388", lane: "YYZ → MIA", m: "14.4%", reason: "Detention fee swallowed" },
            { id: "RY-78201", lane: "YVR → SEA", m: "16.8%", reason: "Fuel surge to $4.18/gal" },
            { id: "RY-78122", lane: "YYC → GTF", m: "17.2%", reason: "Customs hold +24h" },
          ].map((a, i) => (
            <div key={i} className="ry-row" style={{ display: "flex", gap: 10, padding: "10px 0", borderTop: i ? "0.5px solid rgba(255,255,255,0.06)" : "none", animationDelay: `${i * 60}ms` }}>
              <div style={{ width: 28, height: 28, borderRadius: 7, background: `${RED}22`, border: `0.5px solid ${RED}55`, color: RED, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{Icons.alert}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: "white" }}>{a.id} · {a.lane}</span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: RED }}>{a.m}</span>
                </div>
                <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)", marginTop: 2 }}>{a.reason}</div>
              </div>
              <Btn size="sm">Review</Btn>
            </div>
          ))}
        </Card>
      </div>

      <Card title="Margin by Lane (top 8)">
        <Table compact columns={[
          { key: "lane", label: "Lane", bold: true },
          { key: "loads", label: "Loads MTD" },
          { key: "rev", label: "Revenue" },
          { key: "cost", label: "Cost" },
          { key: "margin", label: "Margin", cell: (r) => <span style={{ color: r.c, fontWeight: 800 }}>{r.margin}</span> },
          { key: "trend", label: "Trend", cell: (r) => <Sparkline points={r.spark} color={r.c} width={80} height={22} /> },
          { key: "act", label: "", align: "right", cell: () => <Btn size="sm">Tune</Btn> },
        ]} rows={[
          { lane: "YYZ → ORD", loads: 188, rev: "$498K", cost: "$367K", margin: "26.3%", c: GREEN, spark: [22, 23, 24, 25, 26, 26, 27] },
          { lane: "YUL → JFK", loads: 76, rev: "$218K", cost: "$149K", margin: "31.6%", c: GREEN, spark: [28, 30, 30, 31, 31, 32, 32] },
          { lane: "LAX → PHX", loads: 64, rev: "$118K", cost: "$96K", margin: "18.4%", c: YELLOW, spark: [22, 20, 19, 18, 18, 18, 18] },
          { lane: "MEM → BNA", loads: 42, rev: "$84K", cost: "$65K", margin: "22.8%", c: GREEN, spark: [20, 21, 22, 23, 22, 23, 23] },
          { lane: "YYC → DFW", loads: 38, rev: "$92K", cost: "$82K", margin: "11.2%", c: RED, spark: [18, 16, 14, 13, 12, 11, 11] },
          { lane: "YVR → SEA", loads: 28, rev: "$42K", cost: "$35K", margin: "16.8%", c: YELLOW, spark: [18, 17, 17, 16, 17, 17, 17] },
          { lane: "ATL → DFW", loads: 22, rev: "$58K", cost: "$40K", margin: "31.5%", c: GREEN, spark: [28, 30, 31, 31, 32, 31, 31] },
          { lane: "YYZ → MIA", loads: 18, rev: "$42K", cost: "$36K", margin: "14.4%", c: YELLOW, spark: [22, 20, 18, 16, 15, 14, 14] },
        ]} />
      </Card>
    </div>
  );
}
function MarginDistribution() {
  const buckets = [
    { l: "< 10%", n: 4, c: RED },
    { l: "10-15%", n: 8, c: ORANGE },
    { l: "15-20%", n: 22, c: YELLOW },
    { l: "20-25%", n: 48, c: GREEN },
    { l: "25-30%", n: 32, c: GREEN },
    { l: "30%+", n: 14, c: GREEN },
  ];
  const max = Math.max(...buckets.map((b) => b.n));
  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 160, padding: "10px 0" }}>
        {buckets.map((b, i) => (
          <div key={b.l} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "white" }}>{b.n}</div>
            <div className="ry-bar" style={{ width: "100%", height: `${(b.n / max) * 100}%`, background: `linear-gradient(180deg, ${b.c}, ${b.c}44)`, borderRadius: "4px 4px 0 0", animationDelay: `${i * 80}ms`, boxShadow: `0 0 12px ${b.c}55` }} />
            <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.5)" }}>{b.l}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", lineHeight: 1.5 }}>
        <span style={{ color: RED }}>● 4 loads below floor</span> · <span style={{ color: GREEN }}>94 healthy</span> · <span style={{ color: ORANGE }}>30 marginal</span> — median margin <strong style={{ color: "white" }}>23.4%</strong>
      </div>
    </div>
  );
}

/* ============================================================
   12. CLAIMS & INCIDENTS
   ============================================================ */
export function Claims() {
  return (
    <div>
      <PageHeader kicker="RISK & CLAIMS" title="Claims & Incidents" subtitle="9 open · 3 in dispute · $84K total exposure · 31% loss ratio"
        actions={<><Btn>Risk Report</Btn><Btn primary>+ Open Incident</Btn></>} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 16 }}>
        <KpiTile label="Open" value={9} delta="↗ +2 this week" deltaColor={YELLOW} icon={Icons.gavel} />
        <KpiTile label="Avg Resolution" value="14 days" delta="↓ −3" deltaColor={GREEN} icon={Icons.history} />
        <KpiTile label="Total Exposure" value="$84K" delta="$32K likely recovered" icon={Icons.dollar} />
        <KpiTile label="Loss Ratio" value="31%" delta="Industry avg 58%" deltaColor={GREEN} icon={Icons.shield} />
        <KpiTile hero label="Claims Paid YTD" value="$184K" delta="From premium income $890K" icon={Icons.check} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 14, marginBottom: 14 }}>
        <Card title="By Type">
          {[
            { l: "OS&D (overage, short, damage)", n: 14, pct: 38, c: ORANGE },
            { l: "Cargo damage", n: 8, pct: 22, c: RED },
            { l: "Reefer breakdown", n: 6, pct: 16, c: BLUE },
            { l: "Accident", n: 4, pct: 11, c: RED },
            { l: "Theft", n: 3, pct: 8, c: PURPLE },
            { l: "Other", n: 2, pct: 5, c: "rgba(255,255,255,0.6)" },
          ].map((t, i) => (
            <div key={t.l} className="ry-row" style={{ padding: "8px 0", borderTop: i ? "0.5px solid rgba(255,255,255,0.06)" : "none", animationDelay: `${i * 50}ms` }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 11.5, color: "white" }}>{t.l}</span>
                <span style={{ fontSize: 11.5, color: t.c, fontWeight: 700 }}>{t.n}</span>
              </div>
              <div style={{ height: 4, borderRadius: 2, background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
                <div style={{ width: `${t.pct}%`, height: "100%", background: t.c, borderRadius: 2 }} />
              </div>
            </div>
          ))}
        </Card>
        <Card title="Active Claims Pipeline">
          <PipelineStages />
        </Card>
      </div>
      <Table columns={[
        { key: "id", label: "Claim", bold: true },
        { key: "ship", label: "Shipment" },
        { key: "type", label: "Type", cell: (r) => <Badge color={r.tc}>{r.type}</Badge> },
        { key: "cust", label: "Customer / Driver" },
        { key: "opened", label: "Opened" },
        { key: "exp", label: "Exposure", align: "right", cell: (r) => <span style={{ fontWeight: 700 }}>{r.exp}</span> },
        { key: "stage", label: "Stage", cell: (r) => <Badge color={r.sc}>{r.stage}</Badge> },
        { key: "act", label: "", align: "right", cell: () => <Btn size="sm">Open</Btn> },
      ]} rows={[
        { id: "CL-2025-042", ship: "RY-78422", type: "Cargo damage", tc: RED, cust: "Retail Supply / D. Johnson", opened: "Today", exp: "$2,400", stage: "Investigating", sc: ORANGE },
        { id: "CL-2025-041", ship: "RY-78388", type: "OS&D", tc: ORANGE, cust: "ABC Mfg. / D. Johnson", opened: "May 18", exp: "$840", stage: "Negotiating", sc: BLUE },
        { id: "CL-2025-040", ship: "RY-78201", type: "Reefer breakdown", tc: BLUE, cust: "Sunrise Dist. / D. Johnson", opened: "May 16", exp: "$12,400", stage: "Insurance filed", sc: PURPLE },
        { id: "CL-2025-039", ship: "RY-78122", type: "Accident", tc: RED, cust: "Acme Mfg. / D. Johnson", opened: "May 14", exp: "$48,000", stage: "Adjuster on-site", sc: RED },
        { id: "CL-2025-038", ship: "RY-78088", type: "Theft", tc: PURPLE, cust: "Metro Supply / —", opened: "May 8", exp: "$18,000", stage: "Police report", sc: PURPLE },
        { id: "CL-2025-037", ship: "RY-77998", type: "OS&D", tc: ORANGE, cust: "Northwest Goods / D. Johnson", opened: "May 2", exp: "$420", stage: "Closed", sc: GREEN },
      ]} />
    </div>
  );
}
function PipelineStages() {
  const stages = [
    { k: "Reported", n: 4, c: ORANGE },
    { k: "Investigating", n: 3, c: BLUE },
    { k: "Negotiating", n: 2, c: PURPLE },
    { k: "Insurance Filed", n: 2, c: YELLOW },
    { k: "Closed", n: 6, c: GREEN },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 }}>
      {stages.map((s, i) => (
        <div key={s.k} style={{ background: `${s.c}10`, border: `0.5px solid ${s.c}55`, borderRadius: 10, padding: 12, position: "relative" }}>
          <div style={{ fontSize: 10, color: s.c, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>{s.k}</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: "white", letterSpacing: -0.5, marginTop: 4 }}><CountUp value={s.n} /></div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", marginTop: 2 }}>claims</div>
          {i < stages.length - 1 && <div style={{ position: "absolute", right: -10, top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.3)", zIndex: 1 }}>›</div>}
        </div>
      ))}
    </div>
  );
}

/* ============================================================
   13. USER ROLES & PERMISSIONS MATRIX
   ============================================================ */
export function Roles() {
  const roles = ["Owner", "Admin", "Dispatcher", "Sales", "Accounting", "Driver Mgr", "Viewer"];
  const modules = ["Dashboard", "Shipments", "Quotes", "Bidding", "Dispatch", "Customers", "Drivers", "Documents", "Invoices", "Payouts", "Margins", "Claims", "Reports", "Audit", "Settings"];
  const perms = (mod, role) => {
    if (role === "Owner") return "CRUD";
    if (role === "Viewer") return "R";
    if (role === "Admin") return ["Audit", "Settings"].includes(mod) ? "CRU" : "CRUD";
    if (role === "Dispatcher") return ["Shipments", "Quotes", "Bidding", "Dispatch", "Drivers", "Documents", "Dashboard"].includes(mod) ? "CRUD" : ["Invoices", "Margins", "Claims", "Customers"].includes(mod) ? "R" : "—";
    if (role === "Sales") return ["Quotes", "Customers", "Dashboard", "Reports"].includes(mod) ? "CRUD" : ["Shipments", "Margins", "Invoices"].includes(mod) ? "R" : "—";
    if (role === "Accounting") return ["Invoices", "Payouts", "Margins", "Reports"].includes(mod) ? "CRUD" : ["Shipments", "Customers", "Drivers", "Audit"].includes(mod) ? "R" : "—";
    if (role === "Driver Mgr") return ["Drivers", "Documents", "Payouts"].includes(mod) ? "CRUD" : ["Shipments", "Claims"].includes(mod) ? "R" : "—";
    return "—";
  };
  const cellColor = (p) => p === "CRUD" ? GREEN : p === "CRU" ? ORANGE : p === "R" ? BLUE : "rgba(255,255,255,0.2)";
  return (
    <div>
      <PageHeader kicker="ACCESS CONTROL" title="Roles & Permissions" subtitle={`${roles.length} roles · ${modules.length} modules · last reviewed May 1`}
        actions={<><Btn>Audit Roles</Btn><Btn primary>+ New Role</Btn></>} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 10, marginBottom: 16 }}>
        {[
          { r: "Owner", n: 1, c: ORANGE, d: "Full access" },
          { r: "Admin", n: 2, c: ORANGE, d: "All ops + reporting" },
          { r: "Dispatcher", n: 4, c: BLUE, d: "Ops + drivers" },
          { r: "Sales", n: 3, c: PURPLE, d: "Quotes + customers" },
          { r: "Accounting", n: 2, c: GREEN, d: "AR + payouts" },
          { r: "Driver Mgr", n: 2, c: YELLOW, d: "Driver + comp" },
          { r: "Viewer", n: 5, c: "rgba(255,255,255,0.6)", d: "Read only" },
        ].map((r) => (
          <div key={r.r} style={{ background: "rgba(255,255,255,0.025)", border: `0.5px solid ${r.c}44`, borderRadius: 11, padding: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: r.c }}>{r.r}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "white", marginTop: 2 }}>{r.n}</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", marginTop: 2 }}>{r.d}</div>
          </div>
        ))}
      </div>
      <Card title="Permission Matrix" right={<div style={{ display: "flex", gap: 8, fontSize: 10, color: "rgba(255,255,255,0.6)" }}>
        <Badge color={GREEN}>CRUD</Badge>
        <Badge color={ORANGE}>CRU</Badge>
        <Badge color={BLUE}>R</Badge>
        <Badge color="rgba(255,255,255,0.4)">—</Badge>
      </div>}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "8px 6px", color: "rgba(255,255,255,0.5)", fontSize: 10, letterSpacing: 1, fontWeight: 700, borderBottom: "0.5px solid rgba(255,255,255,0.08)" }}>MODULE</th>
                {roles.map((r) => <th key={r} style={{ padding: "8px 4px", color: "rgba(255,255,255,0.5)", fontSize: 10, letterSpacing: 1, fontWeight: 700, borderBottom: "0.5px solid rgba(255,255,255,0.08)" }}>{r}</th>)}
              </tr>
            </thead>
            <tbody>
              {modules.map((m, i) => (
                <tr key={m}>
                  <td style={{ padding: "7px 6px", fontWeight: 600, color: "white", borderBottom: i === modules.length - 1 ? "none" : "0.5px solid rgba(255,255,255,0.04)" }}>{m}</td>
                  {roles.map((r) => {
                    const p = perms(m, r);
                    return (
                      <td key={r} style={{ padding: 4, textAlign: "center", borderBottom: i === modules.length - 1 ? "none" : "0.5px solid rgba(255,255,255,0.04)" }}>
                        <span style={{ background: p === "—" ? "transparent" : `${cellColor(p)}22`, border: `0.5px solid ${cellColor(p)}66`, color: cellColor(p), padding: "2px 6px", borderRadius: 5, fontSize: 9.5, fontWeight: 700, display: "inline-block", minWidth: 28 }}>{p}</span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/* ============================================================
   14. AUDIT LOG
   ============================================================ */
export function AuditLog() {
  const events = [
    { t: "2 min ago", actor: "Dwayne Johnson", role: "Owner", action: "Updated quote QTE-2052 rate $2,875 → $2,950", target: "Quote", ip: "192.168.1.42", c: ORANGE },
    { t: "8 min ago", actor: "System (AI)", role: "Auto", action: "Generated pricing recommendation for YYZ → ORD", target: "Pricing Engine", ip: "internal", c: PURPLE },
    { t: "12 min ago", actor: "Dwayne Johnson", role: "Admin", action: "Approved payout queue · 148 drivers · $284K", target: "Payouts", ip: "192.168.1.18", c: GREEN },
    { t: "14 min ago", actor: "D. Johnson", role: "Driver", action: "Uploaded BOL for RY-78432", target: "Document", ip: "iOS · Toronto", c: BLUE },
    { t: "22 min ago", actor: "Dwayne Johnson", role: "Customer", action: "Created shipment request via portal", target: "Shipment", ip: "Web · Toronto", c: BLUE },
    { t: "38 min ago", actor: "Dwayne Johnson", role: "Admin", action: "Opened claim CL-2025-042 · $2,400 exposure", target: "Claim", ip: "192.168.1.27", c: RED },
    { t: "1h ago", actor: "Dwayne Johnson", role: "Manager", action: "Granted ACE filing permission to dispatcher role", target: "Permissions", ip: "192.168.1.31", c: YELLOW },
    { t: "1h ago", actor: "Dwayne Johnson", role: "Owner", action: "Adjusted margin floor lane LAX → PHX to 22%", target: "Margin Rules", ip: "192.168.1.42", c: ORANGE },
    { t: "2h ago", actor: "System", role: "Auto", action: "Failed login attempt × 3 for daniel@", target: "Auth", ip: "203.0.113.42 (CN)", c: RED },
    { t: "3h ago", actor: "Dwayne Johnson", role: "Owner", action: "Logged in via MFA (TOTP)", target: "Auth", ip: "192.168.1.42", c: GREEN },
    { t: "4h ago", actor: "API · QuickBooks", role: "Integration", action: "Synced 14 invoices, 8 payments", target: "QB Online", ip: "intuit.api", c: BLUE },
    { t: "5h ago", actor: "Dwayne Johnson", role: "Admin", action: "Exported drivers CSV (148 rows)", target: "Drivers", ip: "192.168.1.18", c: ORANGE },
    { t: "Yesterday", actor: "Dwayne Johnson", role: "Owner", action: "Connected Stripe Billing integration", target: "Settings", ip: "192.168.1.42", c: GREEN },
  ];
  return (
    <div>
      <PageHeader kicker="SECURITY · ACCESS" title="Audit Log" subtitle="Append-only · 12,482 events · SOC2 retention 7 years"
        actions={<><Btn>Filters</Btn><Btn>Export</Btn><Btn primary>{Icons.lock} Lock Window</Btn></>} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 16 }}>
        <KpiTile label="Events Today" value={488} icon={Icons.history} />
        <KpiTile label="Unique Actors" value={42} icon={Icons.users} />
        <KpiTile label="Failed Logins" value={3} delta="↗ from 203.x" deltaColor={RED} icon={Icons.alert} />
        <KpiTile label="Sensitive Actions" value={18} delta="payouts, perms, exports" icon={Icons.shield} />
        <KpiTile hero label="Compliance Score" value="98 / 100" delta="SOC2 ready" icon={Icons.check} />
      </div>
      <ChipRow chips={["All · 488", "Auth", "Shipments", "Payouts", "Permissions", "Integrations", "Failed only"]} />
      <Card>
        <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 140px 100px 110px", gap: 12, padding: "8px 6px", fontSize: 9.5, letterSpacing: 1, color: "rgba(255,255,255,0.5)", fontWeight: 700, borderBottom: "0.5px solid rgba(255,255,255,0.08)" }}>
          <div>WHEN</div><div>ACTION</div><div>TARGET</div><div>ROLE</div><div>IP</div>
        </div>
        {events.map((e, i) => (
          <div key={i} className="ry-row" style={{ display: "grid", gridTemplateColumns: "120px 1fr 140px 100px 110px", gap: 12, padding: "10px 6px", borderBottom: i === events.length - 1 ? "none" : "0.5px solid rgba(255,255,255,0.04)", fontSize: 11.5, alignItems: "center", animationDelay: `${i * 30}ms` }}>
            <div style={{ color: "rgba(255,255,255,0.55)" }}>{e.t}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: e.c, flexShrink: 0, boxShadow: `0 0 6px ${e.c}88` }} />
              <div>
                <div style={{ color: "white" }}>{e.action}</div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginTop: 1 }}>{e.actor}</div>
              </div>
            </div>
            <div><Badge color={e.c}>{e.target}</Badge></div>
            <div style={{ color: "rgba(255,255,255,0.7)" }}>{e.role}</div>
            <div style={{ color: "rgba(255,255,255,0.55)", fontFamily: "JetBrains Mono, monospace", fontSize: 10.5 }}>{e.ip}</div>
          </div>
        ))}
      </Card>
    </div>
  );
}
